import { StampComponents } from '@/components/Pages/home/components/StampComponents'
import { StampSheetSkeleton } from '@/components/Pages/home/components/StampSheetSkeleton'
import { Skeleton } from '@/components/UI/Skeleton'
import { Typography } from '@/components/UI/Typography'
import type { CampaignSheetMode } from '@/schemas/v2/campaign'
import { IconLock } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

interface StampInSheet {
  id: number
  name: string
  spotId: number
  spots: Spot[]
  period: boolean
  point: number
  image: string
  overlayImage?: string | null
  hasStamped?: boolean
  isHas?: boolean
  hasCount?: number
  stampCount?: number
  earnedPoints?: number
}

interface UserSheetObject {
  id: number
  name: string
  description: string
  earnedPoints: number
  startAt: string
  endAt: string
  total: number
  hasCount: number
  backgroundImage: string
  stamps: StampInSheet[]
}

interface Spot {
  address?: string
  id: number
  name: string
  zip?: string
}

export interface StampSheetStampInfo {
  id: number
  name: string
  image: string | null
  point: number
  isHas: boolean
  spots: Spot[]
}

interface StampSheetProps {
  sheet: UserSheetObject
  campaign: {
    isRepeatStamp: boolean
    stampShape: 0 | 1
    sheetMode: CampaignSheetMode
  }
  /** シートが未開放かどうか。true のとき灰色オーバーレイ＋鍵で操作を無効化する */
  isLocked?: boolean
  /** 購入連動アクセス制御によるロック（#3281）。true のとき灰色オーバーレイ＋鍵で操作を無効化する */
  purchaseLocked?: boolean
  /** 購入連動ロック（purchaseLocked）時のオーバーレイ押下で呼ばれる。解除商品の購入導線に使用する */
  onPurchaseUnlock?: () => void
  /** スタンプをタップした時に呼ばれる。ダイアログ表示に使用する */
  onStampClick: (stamp: StampSheetStampInfo) => void
  /**
   * OVERLAY の重ね絵画像が読み込まれた時に呼ばれる。
   * 重ね絵画像は非同期ロードのため、親の Swiper(autoHeight) は初期測定で高さを取りこぼし
   * 下部がクリップされる。ロード後に親が updateAutoHeight() で測り直すために使用する。
   */
  onOverlayImageLoad?: () => void
}

/**
 * campaign.type === 1の、通常のスタンプの場合のシート
 * */
export const StampSheet = ({
  sheet,
  campaign,
  isLocked = false,
  purchaseLocked = false,
  onPurchaseUnlock,
  onStampClick,
  onOverlayImageLoad,
}: StampSheetProps) => {
  const { t } = useTranslation()

  // 開放前ロック: sheetMode に関わらず実スタンプ内容を出さず、スケルトンと同じプレースホルダー＋施錠オーバーレイで統一表示する。
  // 表示するプレースホルダー数は totalCount（sheet.total）に合わせ、最大 9 個（3列×3行）に制限する。
  if (isLocked) {
    return (
      <StampSheetSkeleton
        stampCount={Math.min(sheet.total, 9)}
        animate={false}
        overlay={
          <div className="absolute inset-0 z-10 bg-black/40 flex flex-col items-center justify-center rounded-lg pointer-events-none">
            <IconLock size={48} className="text-white" aria-hidden="true" />
          </div>
        }
      />
    )
  }

  // 購入連動ロック: シート全面に灰色透明オーバーレイ＋中央に鍵。押下で解除商品の購入導線を起動する
  const purchaseLockOverlay = (
    <button
      type="button"
      onClick={onPurchaseUnlock}
      aria-label={t('home.purchase_unlock.aria', '購入して解除する')}
      className="absolute inset-0 z-20 flex w-full items-center justify-center rounded-lg bg-gray-500/50"
    >
      <IconLock size={48} className="text-white" aria-hidden="true" />
    </button>
  )

  // 購入連動ロック時は全モード共通で、シート名＋totalCount 分（最大9個）のスケルトンスタンプを表示する。
  // 実データのスタンプはサーバ側で隠されて空になるため、プレースホルダーで枠を保つ。
  if (purchaseLocked) {
    return (
      <div className="rounded-lg px-4 py-6 border border-[#cbcbce] relative overflow-hidden bg-white">
        {purchaseLockOverlay}
        <div className="relative">
          <Typography variant="body4" component="h2" className="text-center">
            {sheet.name}
          </Typography>
        </div>
        <div className="mt-4 grid grid-cols-3 place-items-center gap-y-4">
          {Array.from({ length: Math.min(sheet.total, 9) }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: 静的なプレースホルダー行のため index を key に使用
            <div key={i} className="flex w-full flex-col items-center gap-1">
              <Skeleton variant="circle" width={60} />
              <Skeleton variant="text" width="70%" height={12} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (campaign.sheetMode === 'NORMAL' || campaign.sheetMode === 'BACKGROUND') {
    // それぞれの場合に応じたstyleを設定
    const style =
      campaign.sheetMode === 'NORMAL'
        ? { backgroundColor: '#ffffff' }
        : campaign.sheetMode === 'BACKGROUND'
          ? {
              backgroundImage: `url("${sheet.backgroundImage}")`,
              backgroundRepeat: 'repeat-y',
              backgroundSize: '100% auto',
            }
          : {}
    return (
      <div
        key={`div-${sheet.id}`}
        className="rounded-lg px-4 py-6 border border-[#cbcbce] relative overflow-hidden"
        style={style}
      >
        <div className="relative">
          <Typography variant="body4" component="h2" className="text-center">
            {sheet.name}
          </Typography>
        </div>
        <div className="mt-1 flex items-center justify-end">
          <Typography variant="body3">
            {campaign.isRepeatStamp
              ? `${t('home.you_got', '獲得数')}： ${sheet.hasCount}${t('home.count_unit', '個')}`
              : `${t('home.achievement_count', '達成数')}： ${sheet.hasCount} / ${sheet.total}`}
          </Typography>
        </div>
        {/* gap-y-4 が各スタンプの行間を担う（旧: StampComponents 側の pt-4） */}
        <div className="mt-4 grid grid-cols-3 items-start justify-items-center gap-y-4">
          {sheet.stamps?.map((contentStamp, i: number) => {
            const stampShape: 'circular' | 'square' = campaign.stampShape === 1 ? 'square' : 'circular'
            return (
              <StampComponents
                key={`slide-${sheet.id}-stamp-cmp-${contentStamp.spotId}-${i}`}
                query={contentStamp.spotId}
                hasCount={contentStamp.hasCount ?? 0}
                course={sheet.name}
                img={contentStamp.image}
                name={contentStamp.name}
                spots={contentStamp.spots as [{ address: string; id: number; name: string; zip: string }]}
                isRepeatStamp={campaign?.isRepeatStamp}
                stampShape={stampShape}
                onGroupModal={() =>
                  onStampClick({
                    id: contentStamp.id,
                    name: contentStamp.name,
                    image: contentStamp.image,
                    point: contentStamp.point,
                    isHas: contentStamp.isHas ?? false,
                    spots: contentStamp.spots,
                  })
                }
              />
            )
          })}
        </div>
      </div>
    )
  }
  if (campaign.sheetMode === 'OVERLAY') {
    return (
      <div className="rounded-lg border border-[#cbcbce] relative overflow-hidden">
        {/*
          重ね絵：背景画像を幅いっぱい（w-full）に表示して画像全体を見せ、
          取得済み overlayImage を同じ領域に重ねる。シート名・達成状況・スタンプグリッドは表示しない（#3124）。
        */}
        <div className="relative w-full">
          {sheet.backgroundImage && (
            <img
              key={`img-1-${sheet.id}`}
              src={sheet.backgroundImage}
              alt={''}
              className="block w-full h-auto"
              onLoad={onOverlayImageLoad}
            />
          )}
          {sheet.stamps?.map((v) => {
            if ((v.hasCount ?? 0) < 1 || !v.overlayImage) return null
            return (
              <img
                key={v.name}
                src={v.overlayImage}
                alt={v.name}
                className="absolute inset-0 w-full h-full object-contain"
              />
            )
          })}
        </div>
      </div>
    )
  }
}
